import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PropTypes from 'prop-types';

const ActivityStats = ({ activities }) => {
  // Calculate statistics
  const totalActivities = activities.length;
  const upcomingActivities = activities.filter(activity => new Date(activity.date) >= new Date()).length;
  const pastActivities = activities.filter(activity => new Date(activity.date) < new Date()).length;
  
  // Group activities by category
  const activitiesByCategory = activities.reduce((acc, activity) => {
    acc[activity.category] = (acc[activity.category] || 0) + 1;
    return acc;
  }, {});

  // Calculate total participants
  const totalParticipants = activities.reduce((sum, activity) => sum + (activity.numberOfParticipants || 1), 0);

  // Get most recent activity
  const mostRecentActivity = activities
    .filter(activity => new Date(activity.date) < new Date())
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

  // Get next activity
  const nextActivity = activities
    .filter(activity => new Date(activity.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        {/* Overview Stats */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Vue d'ensemble
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total des activités
                </Typography>
                <Typography variant="h4">
                  {totalActivities}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total des participants
                </Typography>
                <Typography variant="h4">
                  {totalParticipants}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Upcoming vs Past */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Répartition
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Activités à venir
                </Typography>
                <Typography variant="h4" color="primary">
                  {upcomingActivities}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Activités passées
                </Typography>
                <Typography variant="h4" color="text.secondary">
                  {pastActivities}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Répartition par catégorie
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(activitiesByCategory).map(([category, count]) => (
                <Grid item xs={6} sm={4} md={3} key={category}>
                  <Typography variant="body2" color="text.secondary">
                    {category}
                  </Typography>
                  <Typography variant="h5">
                    {count}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Recent and Next Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dernière activité
            </Typography>
            {mostRecentActivity ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {mostRecentActivity.title}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(mostRecentActivity.date), 'd MMMM yyyy', { locale: fr })}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune activité passée
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Prochaine activité
            </Typography>
            {nextActivity ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {nextActivity.title}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(nextActivity.date), 'd MMMM yyyy', { locale: fr })}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune activité à venir
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

ActivityStats.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      numberOfParticipants: PropTypes.number,
    })
  ).isRequired,
};

export default ActivityStats; 